<script>
import { format, isValid, parseISO } from 'date-fns'
import { useField } from 'vee-validate'
import { computed, inject, ref, watch } from 'vue'

const toDate = (input) => {
  if (input instanceof Date) return isValid(input) ? input : null
  if (typeof input !== 'string' || input === '') return null

  const parsed = parseISO(input.includes('T') ? input.split('T')[0] : input)

  return isValid(parsed) ? parsed : null
}

const toIsoDay = (date) => (date == null ? null : format(date, 'yyyy-MM-dd'))

export default {
  name: 'appDateRange',

  props: {
    className: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    modelValue: {
      type: Array,
      default: null
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'DD/MM/YYYY - DD/MM/YYYY'
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },

  setup(props, { emit }) {
    const { value, errorMessage, handleChange } = useField(() => props.name, props.rules, {
      syncVModel: true,
      validateOnValueUpdate: false
    })

    const alreadySubmittedOnce = inject('alreadySubmittedOnce', ref(false))

    const range = ref(null)

    const toRange = (pair) => {
      if (!Array.isArray(pair)) return null

      const start = toDate(pair[0])
      const end = toDate(pair[1])

      return start == null || end == null ? null : { start, end }
    }

    const rangeToPair = (value) =>
      value?.start == null || value?.end == null
        ? null
        : [toIsoDay(value.start), toIsoDay(value.end)]

    const modelToPair = (value) =>
      Array.isArray(value) ? [toIsoDay(toDate(value[0])), toIsoDay(toDate(value[1]))] : null

    const pairKey = (pair) => {
      const [start, end] = Array.isArray(pair) ? pair : []

      return `${start ?? ''}|${end ?? ''}`
    }

    watch(
      () => props.modelValue,
      (newValue) => {
        const incoming = toRange(newValue)

        if (pairKey(rangeToPair(incoming)) === pairKey(rangeToPair(range.value))) return

        range.value = incoming
      },
      { immediate: true }
    )

    watch(range, (newRange) => {
      const pair = rangeToPair(newRange)

      if (pairKey(pair) === pairKey(modelToPair(props.modelValue))) return

      emit('update:modelValue', pair)
      handleChange(pair, alreadySubmittedOnce.value)
    })

    const valueInput = computed(() => {
      const current = toRange(value.value) ?? range.value

      if (current == null) return props.placeholder

      return `${format(current.start, 'dd/MM/yyyy')} - ${format(current.end, 'dd/MM/yyyy')}`
    })

    const maxDate = computed(() => toDate(props.config?.maxDate) ?? null)

    return {
      range,
      value,
      valueInput,
      maxDate,
      errorMessage
    }
  }
}
</script>

<template>
  <div :class="className">
    <label :for="id">{{ label }}</label>
    <i v-if="icon" :class="icon"></i>
    <VDatePicker v-model.range="range" :max-date="maxDate">
      <template #default="{ togglePopover }">
        <input
          :id="id"
          :name="name"
          :value="valueInput"
          type="button"
          :disabled="isDisabled"
          :class="{ invalid: isInvalid || !!errorMessage }"
          @click="togglePopover"
        />
      </template>
    </VDatePicker>
    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
  </div>
</template>

<style lang="scss">
.vc-header {
  .vc-title,
  .vc-arrow {
    background-color: transparent;
  }
}
.vc-popover-content-wrapper {
  z-index: 999;
}
</style>
